// @ts-check
/**
 * @file reminders.js
 * @location src/utils/reminders.js
 * @role 팀원 담당 업무의 기한/지정일 임박 리마인더 — NotificationBell 인앱.
 *   대인 독촉 아님(razor ② 무관) · 본인 마감 리마인더. 파생 계산(영속·백엔드·스케줄러 불필요).
 *   읽음(acknowledgedAt) 한정 — 안 읽음은 업무 도착(MemberInbox)에서 surfacing되므로 중복 방지.
 */
import { isClosed } from '../data/labels.js';
import { getTodayStr, addDays } from './dateUtils.js';

/**
 * 임박 단계 — 1일 이내=urgent / 3일 이내=near / 7일 이내=soon.
 * @param {number} daysLeft
 * @returns {'urgent'|'near'|'soon'}
 */
export function reminderTier(daysLeft) {
  if (daysLeft <= 1) return 'urgent';
  if (daysLeft <= 3) return 'near';
  return 'soon';
}

/**
 * 본인(uid) 담당·미완·읽음 task 중 due 가 오늘 기준 1~7일 후(미래)인 것.
 *   due===today·과거·due 없음 제외 — 임박(곧 도래) 리마인더만. daysLeft 오름차순.
 * @param {import('../data/types.js').Task[]} tasks
 * @param {string} uid
 * @param {string} [today] YYYY-MM-DD
 * @returns {Array<import('../data/types.js').Task & { _daysLeft: number, _tier: 'urgent'|'near'|'soon' }>}
 */
export function upcomingReminders(tasks, uid, today = getTodayStr()) {
  const max = addDays(today, 7);
  const out = [];
  for (const t of tasks) {
    if (t.aee !== uid || isClosed(t.status) || !t.acknowledgedAt) continue;
    if (!t.due || t.due <= today || t.due > max) continue;
    const daysLeft = Math.round(
      (new Date(`${t.due}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86400000,
    );
    out.push({ ...t, _daysLeft: daysLeft, _tier: reminderTier(daysLeft) });
  }
  return out.sort((a, b) => a._daysLeft - b._daysLeft);
}

/**
 * dateKind 별 리마인더 문구.
 *   ondate(지정일) → 「예정이 다가오고 있습니다」 / deadline(기한·폴백) → 「기한까지 N일 남음」
 * @param {{ dateKind?: string, _daysLeft: number }} t
 * @returns {string}
 */
export function reminderText(t) {
  if (t.dateKind === 'ondate') return '예정이 다가오고 있습니다';
  return `기한까지 ${t._daysLeft}일 남음`;
}
